/**
 * Number handling shared by the Player's slider + typed-value controls: parse what the owner types
 * (comma or dot), clamp to the control's range, round to its step. Pure, so it runs under node:test.
 */

export interface NumericRange {
  min: number;
  max: number;
  step: number;
}

/** Decimal digits of a step: 0.01 -> 2, 0.5 -> 1, 1 -> 0. */
export function decimalsOf(step: number): number {
  const text = String(step);
  return text.split(".")[1]?.length ?? 0;
}

/** Rounds to the step's decimals (no float noise: 0.1 + 0.2 -> 0.3). */
export function roundTo(value: number, step: number): number {
  return Number(value.toFixed(decimalsOf(step)));
}

/** Clamps to min..max, then rounds to the step. Not finite -> `fallback`. */
export function clampRound(range: NumericRange, value: number, fallback = range.min): number {
  if (!Number.isFinite(value)) return fallback;
  return roundTo(Math.min(range.max, Math.max(range.min, value)), range.step);
}

/** Typed text -> number: "0,35" and "0.35" both work. Anything else (empty, "abc", "1e3", "1.2.3") -> undefined. */
export function parseDecimal(text: string): number | undefined {
  const t = text.trim().replace(",", ".");
  if (!/^[+-]?(\d+\.?\d*|\.\d+)$/.test(t)) return undefined;
  const value = Number(t);
  return Number.isFinite(value) ? value : undefined;
}

/** Fixed decimals of the step: 0.35 -> "0.35", 0.4 -> "0.40". */
export function formatValue(range: NumericRange, value: number): string {
  return value.toFixed(decimalsOf(range.step));
}

/** Result of a typed commit: the accepted (clamped, rounded) value, or undefined to revert. */
export function commitTyped(range: NumericRange, text: string): number | undefined {
  const parsed = parseDecimal(text);
  return parsed === undefined ? undefined : clampRound(range, parsed);
}
